import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Checkbox,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatDateCompact, formatTime, getUserDateFormat } from "../../utils/formatDate";
import { format } from "date-fns";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";

const AlertItem = ({
  alert,
  icon,
  isSelected,
  onSelect,
  onDelete,
  onMarkAsRead,
  onMarkAsUnread,
  onToggleStar,
}) => {
  const [expanded, setExpanded] = useState(false);
  const wasManuallyMarkedUnread = useRef(false);
  const isExpanding = useRef(false);

  useEffect(() => {
    if (!alert.isRead && expanded && !isExpanding.current) {
      setExpanded(false);
      wasManuallyMarkedUnread.current = true;
    }
  }, [alert.isRead, expanded]);

  const toggleExpand = async (event) => {
    event.stopPropagation();
    isExpanding.current = true;
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);

    // If alert is unread and it was not manually marked unread, mark as read
    if (nextExpanded && !alert.isRead && !wasManuallyMarkedUnread.current) {
      if (onMarkAsRead) {
        await onMarkAsRead(alert.id);
      }
    }

    // Reset refs after expansion
    wasManuallyMarkedUnread.current = false;
    isExpanding.current = false;
  };

  const handleSelect = (event) => {
    event.stopPropagation();
    if (onSelect) {
      onSelect(alert.id, !isSelected);
    }
  };

  const handleStarToggle = async (event) => {
    event.stopPropagation();
    if (onToggleStar) {
      await onToggleStar(alert.id, !alert.isStarred);
    }
  };

  const handleDelete = async (event) => {
    event.stopPropagation();
    if (onDelete) {
      await onDelete(alert.id);
    }
  };

  // Updated formatDate function using dynamic formatting
  const formatDateDisplay = (dateString) => {
    try {
      if (!dateString) return '';
      return formatDateCompact(dateString);
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString || '';
    }
  };

  // Helper function to format date without time (same format as compact but date only)
  const formatDateOnly = (dateString) => {
    try {
      if (!dateString) return '';
      const dateObj = typeof dateString === 'string' ? new Date(dateString) : dateString;
      if (isNaN(dateObj.getTime())) return '';

      const userDateFormat = getUserDateFormat();

      // Use the same compact format as formatDateCompact but without time
      let compactFormat;
      switch (userDateFormat) {
        case 'DD/MM/YYYY':
          compactFormat = 'dd MMM yyyy';
          break;
        case 'MM/DD/YYYY':
          compactFormat = 'MMM dd, yyyy';
          break;
        case 'YYYY/MM/DD':
          compactFormat = 'yyyy MMM dd';
          break;
        default:
          compactFormat = 'dd MMM yyyy';
      }

      return format(dateObj, compactFormat);
    } catch (error) {
      console.error('Error formatting date only:', error);
      return dateString || '';
    }
  };

  const safeAlert = {
    id: alert?.id || '',
    subject: alert?.subject || 'No Subject',
    body: alert?.body || alert?.message || alert?.description || 'No content',
    createdAt: alert?.createdAt || new Date(),
    isRead: alert?.isRead || false,
    isStarred: alert?.isStarred || false,
    userName: alert?.userName || alert?.userId || '',
    ...alert
  };

  return (
    <Box
      sx={{
        backgroundColor: safeAlert.isRead ? "#ffffff" : "#E8EAEE",
        border: expanded ? "1px solid #A5DCF3" : "",
        marginBottom: "8px",
        borderRadius: "8px",
        overflowY: 'auto',
        overflowX: "scroll",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        "&:last-child": {
          borderBottom: "none",
          marginBottom: 0
        },
        cursor: "pointer",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }
      }}
      onClick={toggleExpand}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <Checkbox
            size="small"
            checked={Boolean(isSelected)}
            onChange={handleSelect}
            onClick={(e) => e.stopPropagation()}
            sx={{ mr: 1 }}
          />
          <Checkbox
            size="small"
            checked={Boolean(safeAlert.isStarred)}
            onChange={handleStarToggle}
            onClick={(e) => e.stopPropagation()}
            icon={<StarBorderIcon />}
            checkedIcon={<StarIcon sx={{ color: "#FFD700" }} />}
            sx={{ mr: 1 }}
          />
          <Box sx={{ mr: 2 }}>{icon}</Box>
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: safeAlert.isRead ? 400 : 600,
                color: "#1a1a1a",
                fontSize: expanded ? "24px" : "20px"
              }}
            >
              {safeAlert.subject}
            </Typography>
            {!expanded && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "500px"
                }}
              >
                {safeAlert.body}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="caption" color="textSecondary" sx={{ minWidth: 120 }}>
            {formatDateDisplay(safeAlert.createdAt)}
          </Typography>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleExpand(e); }}>
            <ExpandMoreIcon
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s"
              }}
            />
          </IconButton>
        </Box>
      </Box>
      {expanded && (
        <Box sx={{ px: 4, py: 2, backgroundColor: "#fafbfc" }}>
          <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-line" }}>
            {safeAlert.body}
          </Typography>
          <Stack direction="row" spacing={4} mt={2}>
            <Typography variant="body2" color="textSecondary">
              <strong>Date:</strong> {formatDateOnly(safeAlert.createdAt)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Time:</strong> {formatTime(safeAlert.createdAt)}
            </Typography>
          </Stack>
          {safeAlert.userName && (
            <Typography variant="body2" color="textSecondary" mt={1}>
              <strong>User:</strong> {safeAlert.userName}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AlertItem;